// The words and marks on the board (task 0FU6KKpdmvGeGMjtuhAZ, 1.3).
// Colour and sign always travel with a word, never alone — a cell must
// answer "kim, ne durumda, para var mı" at a glance, without reading a
// sentence (day-view.md §6). Everything the grid prints comes from
// here, so the vocabulary changes in one place.

import type {
  BoardBirthday, BoardMoney, CancelledBy, CellFlag, CellStatus, ConfirmOutcome, SessionHistoryRow,
} from "./api/contract";

export interface Sign {
  icon: string;
  word: string;
}

// The status chip: icon + word, in the task's own words
// (planlı / ✔ onaylı / ✖ gelmiyor / kaydedildi / ödendi / iptal).
export const STATUS_TR: Record<CellStatus, Sign> = {
  planned: {icon: "○", word: "planlı"},
  confirmed: {icon: "✔", word: "onaylı"},
  declined: {icon: "✖", word: "gelmiyor"},
  recorded: {icon: "☑", word: "kaydedildi"},
  paid: {icon: "₺", word: "ödendi"},
  cancelled: {icon: "–", word: "iptal"},
  paused: {icon: "‖", word: "ara"},
};

export const FLAG_TR: Record<CellFlag, Sign> = {
  unrecorded_past: {icon: "⚠", word: "kayıt yok"},
  partially_recorded: {icon: "◐", word: "kısmen kayıtlı"},
  unresolved_client: {icon: "?", word: "danışan eşleşmedi"},
  still_on_calendar: {icon: "⚠", word: "takvimde duruyor"},
};

export const CANCELLED_BY_TR: Record<string, string> = {
  client: "veli", provider: "danışman", clinic: "klinik",
};

export const trNumber = (n: number): string =>
  n.toLocaleString("tr-TR", {maximumFractionDigits: 2});

export const trTL = (n: number): string => `${trNumber(n)} TL`;

// Gizli mod: what stands where a figure would — one * per digit of
// the whole lira, so "4.500" reads as **** and "12.000" as *****, and
// the eye still tells a session fee from a package. maskTL does the
// same inside a sentence the door wrote ("Alınacak: 4.000,00 TL (24.09
// seansı).") for every number that carries a TL after it.
export const maskNum = (n: number): string =>
  "*".repeat(String(Math.abs(Math.trunc(n))).length);
export const maskTL = (s: string): string =>
  s.replace(/(\d[\d.]*)(?:,\d+)?\s*TL\b/g,
    (_, whole: string) => `${"*".repeat(whole.replace(/\./g, "").length)} TL`);

// The money mark beside a name: what to collect from this family right
// now, or that they are in credit — the same collectNow the checkout
// reads to Mükü. Nothing when the account is square. Money holders
// only; the board strips `money` for everyone else. Blind: the figure
// becomes its mask, the word stays.
export type MoneyMark =
  | {kind: "collect"; text: string; title: string}
  | {kind: "credit"; text: string; title: string};

export function moneyMark(m: BoardMoney | undefined, blind = false): MoneyMark | null {
  if (!m) return null;
  const num = (n: number) => blind ? maskNum(n) : trNumber(n);
  const tl = (n: number) => `${num(n)} TL`;
  if (m.collectNowTL > 0) {
    return {
      kind: "collect",
      text: `${num(m.collectNowTL)} alınacak`,
      title: `Alınacak: ${tl(m.collectNowTL)}` +
        (m.restrictedCreditTL > 0 ?
          ` · ${tl(m.restrictedCreditTL)} kredi danışmana kilitli` : ""),
    };
  }
  if (m.creditTL > 0) {
    return {kind: "credit", text: "kredi", title: `Ön ödeme: ${tl(m.creditTL)} kredi`};
  }
  return null;
}

// What the desk takes from this family today, in TL — the figure the
// pay sheet offers and the drawer heads its money card with. The
// checkout's collectNow is the past only: it does not know about the
// seans in front of the desk. While that seans is still to be
// recorded, today's take is its fee plus what earlier seanslar left
// open, minus the credit that can pay for it (the unrestricted credit
// and the credit locked to this danışman); once it is recorded, or
// cancelled, the open balance alone. null when the pair has no fee.
export interface DueInput {
  feeTL: number | null;
  collectNowTL: number;
  creditTL: number;
}
export interface Due {
  amountTL: number | null;
  // The arithmetic in words when it is more than the fee alone
  // ("3.200 ücret + 1.500 önceki − 0 kredi"); null when it is the fee.
  how: string | null;
}
export function dueToday(m: DueInput, lockedHereTL: number, sessionOpen: boolean): Due {
  if (!sessionOpen) return {amountTL: m.collectNowTL, how: null};
  if (m.feeTL === null) return {amountTL: null, how: null};
  // Credit locked to this danışman pays this seans and nothing else
  // (derive.ts never nets it into collectNow), so only the fee's worth
  // of it comes off; the rest stays locked.
  const lockedUsedTL = Math.min(lockedHereTL, m.feeTL);
  const raw = kurus(m.feeTL - lockedUsedTL + m.collectNowTL - m.creditTL);
  const leftTL = kurus(Math.max(0, -raw) + lockedHereTL - lockedUsedTL);
  const parts = [`${trNumber(m.feeTL)} ücret`];
  if (m.collectNowTL > 0) parts.push(`+ ${trNumber(m.collectNowTL)} önceki`);
  if (m.creditTL > 0) parts.push(`− ${trNumber(m.creditTL)} kredi`);
  if (lockedUsedTL > 0) parts.push(`− ${trNumber(lockedUsedTL)} kilitli kredi`);
  const how = parts.length === 1 ? null :
    parts.join(" ") + (leftTL > 0 ? ` (kalan kredi ${trTL(leftTL)})` : "");
  return {amountTL: Math.max(0, raw), how};
}

// ── The pay sheet (A3, Onur 13.10.2026) ───────────────────
// One card: the figure the desk takes today, the button that takes
// it, and the account it comes from — this seans, what earlier
// seanslar left open, and the credit that can pay for it (the
// unrestricted credit plus what is locked to this danışman, netted
// the way dueToday nets them). Only for a seans still to be recorded.
export type PayKind =
  | "take" // money is due today: the button takes dueTL
  | "credit" // credit pays for the seans: the button takes nothing
  | "free"; // a pair with a fee of 0 and nothing open: nothing to take
export interface PayPlan {
  kind: PayKind;
  feeTL: number;
  earlierTL: number; // önceki — earlier seanslar left open
  creditTL: number; // kredi: unrestricted plus locked to this danışman
  // What leaves no debt: fee − the locked credit it uses + önceki −
  // unrestricted credit. Below 0 is unrestricted credit left after.
  owedTL: number;
  lockedLeftTL: number; // locked credit this seans does not use
  dueTL: number; // "Bugün alınacak", never below 0
}
export function payPlan(m: BoardMoney): PayPlan | null {
  if (m.feeTL === null) return null;
  // Locked credit pays this seans only, never another danışman's
  // open seanslar — the part of it above the fee stays locked.
  const lockedUsedTL = Math.min(m.providerCreditTL, m.feeTL);
  const owedTL = kurus(m.feeTL - lockedUsedTL + m.collectNowTL - m.creditTL);
  const dueTL = Math.max(0, owedTL);
  const kind: PayKind = dueTL > 0 ? "take" : m.feeTL > 0 ? "credit" : "free";
  return {
    kind, feeTL: m.feeTL, earlierTL: m.collectNowTL,
    creditTL: kurus(m.creditTL + m.providerCreditTL), owedTL,
    lockedLeftTL: kurus(m.providerCreditTL - lockedUsedTL), dueTL,
  };
}

// What taking an amount leaves: an open balance, a square account, or
// credit (both kinds, as the strip counts it) — the line under the
// figure while the desk types another one, and at rest with 0.
export type PayOutcome =
  | {kind: "owe"; leftTL: number}
  | {kind: "even"}
  | {kind: "credit"; creditTL: number};
export function payOutcome(plan: PayPlan, amountTL: number): PayOutcome {
  const after = kurus(amountTL - plan.owedTL);
  if (after < 0) return {kind: "owe", leftTL: -after};
  const creditTL = kurus(after + plan.lockedLeftTL);
  if (creditTL === 0) return {kind: "even"};
  return {kind: "credit", creditTL};
}

// Sums of kuruş amounts, rounded back to kuruş: 826,04 + 12.813,08
// must equal 13.639,12, not miss it by a float's breath.
function kurus(n: number): number {
  return Math.round(n * 100) / 100 || 0;
}

// ── The seans drawer (v3, Onur 13.10.2026) ────────────────
// The first chip in the drawer's head: where the seans stands, the
// grid's status in a word (Onur 14.10.2026: "is this session done?"
// must not take reading). Recorded is "Seans yapıldı", paid or not —
// the money card says how it was paid. The board's "kısmen kayıtlı"
// and "kayıt yok" come before what the family said: the desk has a
// record to make. A confirmation says how it came when it is one
// child's (`source`).
export type SeansStateKind =
  | "cancelled" | "paused" | "done" | "partial" | "unrecorded" | "declined" | "confirmed"
  | "planned";
export interface SeansState {
  kind: SeansStateKind;
  word: string;
}
export function seansState(status: CellStatus, flags: readonly CellFlag[],
  source: "phone" | "whatsapp" | null = null): SeansState {
  if (status === "cancelled") return {kind: "cancelled", word: "İptal edildi"};
  if (status === "paused") return {kind: "paused", word: "Ara verildi"};
  if (status === "paid" || status === "recorded") return {kind: "done", word: "Seans yapıldı"};
  if (flags.includes("partially_recorded")) return {kind: "partial", word: "Kısmen kayıtlı"};
  if (flags.includes("unrecorded_past")) return {kind: "unrecorded", word: "Kaydı yok"};
  if (status === "declined") return {kind: "declined", word: "Gelmiyor"};
  if (status === "confirmed") {
    return {kind: "confirmed", word: source === "phone" ? "Geliyor · telefon" :
      source === "whatsapp" ? "Geliyor · WhatsApp" : "Geliyor"};
  }
  return {kind: "planned", word: "Planlı"};
}

// The chips under the fee: earlier debt in red, credit in green,
// nothing when the account is square. While the seans is open the
// debt is "önceki" — collectNow does not know this seans; once it is
// recorded it may hold it, so it is the open debt. The credit is what
// can pay this seans: the unrestricted, and what is locked to this
// danışman (the pay sheet's strip counts it the same way).
export interface AcctMark {
  kind: "debt" | "credit";
  amountTL: number;
  word: string;
}
export function acctMarks(m: BoardMoney | undefined, open: boolean): AcctMark[] {
  if (!m) return [];
  const out: AcctMark[] = [];
  if (m.collectNowTL > 0) {
    out.push({kind: "debt", amountTL: m.collectNowTL, word: open ? "önceki borç" : "açık borç"});
  }
  const creditTL = kurus(m.creditTL + m.providerCreditTL);
  if (creditTL > 0) out.push({kind: "credit", amountTL: creditTL, word: "kredi"});
  return out;
}

// 1 = Pazartesi … 7 = Pazar (WEEKDAY_TR's count) for a YYYY-MM-DD.
export function weekdayOf(date: string): number {
  const d = new Date(`${date}T12:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}
// "Ekim 2026" for "2026-10" — the history sheet's months.
export const MONTH_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim",
  "Kasım", "Aralık",
];
export const trMonth = (ym: string): string =>
  `${MONTH_TR[Number(ym.slice(5, 7)) - 1] ?? ym.slice(5, 7)} ${ym.slice(0, 4)}`;

// "07.10 Çarşamba"
export const trDayName = (date: string): string =>
  `${trDay(date)} ${WEEKDAY_TR[weekdayOf(date)]}`;

// A birthday near the seans, said from today — the board may be another
// day's: "Doğum günü bugün · 7 yaşında", "Doğum günü 26 Ekim, 3 gün
// sonra · 7 yaşına giriyor", "Doğum günü dündü · 7 yaşına girdi".
export function birthdayWords(b: BoardBirthday, today: string): string {
  const days = Math.round(
    (Date.parse(`${b.date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 864e5);
  if (days === 0) return `Doğum günü bugün · ${b.age} yaşında`;
  const turns = days > 0 ? `${b.age} yaşına giriyor` : `${b.age} yaşına girdi`;
  if (days === 1) return `Doğum günü yarın · ${turns}`;
  if (days === -1) return `Doğum günü dündü · ${turns}`;
  const day = `${Number(b.date.slice(8, 10))} ${MONTH_TR[Number(b.date.slice(5, 7)) - 1]}`;
  return days > 0 ? `Doğum günü ${day}, ${days} gün sonra · ${turns}` :
    `Doğum günü ${day}, ${-days} gün önceydi · ${turns}`;
}

// The drawer's time line: the series while this seans keeps to it
// ("Her Çarşamba 14:00"), else the day's own time ("Bugün 15:30",
// "15.10 Perşembe 15:30") — a moved seans must not read as its series.
export function whenLine(
  s: {dayOfWeek: number; time: string; recurrence: string} | null,
  date: string, time: string, today: string,
): string {
  if (s && s.time === time && s.dayOfWeek === weekdayOf(date)) {
    const day = WEEKDAY_TR[s.dayOfWeek];
    if (s.recurrence === "weekly") return `Her ${day} ${time}`;
    if (s.recurrence === "biweekly") return `İki haftada bir ${day} ${time}`;
  }
  return date === today ? `Bugün ${time}` : `${trDayName(date)} ${time}`;
}

// Has the seans begun — after which the door refuses the phone
// confirmation and the WhatsApp send, so the drawer stops offering
// them. nowMin is the minute of the Istanbul day it is on `today`.
export function hasStarted(date: string, time: string, today: string, nowMin: number): boolean {
  if (date !== today) return date < today;
  return nowMin >= Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

// The WhatsApp chip's word: the answer, or where the message got to.
// Nothing when nothing was sent — or when the desk's phone call is
// the answer (the Geliyor chip says that).
export function waWord(state: string, source?: string | null):
  {word: string; tone: "ok" | "bad" | "ask" | null} | null {
  switch (state) {
  case "none": return null;
  case "confirmed": return source === "phone" ? null : {word: "onayladı", tone: "ok"};
  case "declined": return {word: "gelmiyor", tone: "bad"};
  case "text": return {word: "yanıt yazdı", tone: "ask"};
  case "failed": return {word: "gönderilemedi", tone: "bad"};
  case "pending": return {word: "gönderiliyor", tone: null};
  case "sent":
  case "delivered":
  case "read":
    return {word: "yanıt yok", tone: null};
  default: return {word: state, tone: null};
  }
}

// The words the WhatsApp chip carries for the whole seans: each
// child's (a group's say whose), the urgent first — "gelmiyor", a send
// that failed and a written reply before an onay — so one child's
// answer is not lost in a group. A phone confirmation has its word
// here only where no Geliyor chip says it (`coming` false: after the
// start, on an iptal, once recorded).
export interface WaChipRow {
  who: string | null;
  state: string;
  source?: string | null;
  coming: boolean;
}
export type WaChipWord = {who: string | null} & NonNullable<ReturnType<typeof waWord>>;
const WA_RANK = {bad: 0, ask: 1, ok: 2};
export function waChipWords(rows: WaChipRow[]): WaChipWord[] {
  const out: WaChipWord[] = [];
  for (const r of rows) {
    const w = waWord(r.state, r.source) ??
      (r.state === "confirmed" && !r.coming ? {word: "telefonda onaylandı", tone: "ok" as const} : null);
    if (w) out.push({who: r.who, ...w});
  }
  return out.sort((a, b) => (a.tone ? WA_RANK[a.tone] : 3) - (b.tone ? WA_RANK[b.tone] : 3));
}

// "şehir dışı" → "Şehir dışı", the way a chip starts its word.
export const capTr = (s: string): string =>
  s.charAt(0).toLocaleUpperCase("tr-TR") + s.slice(1);

// A name in the dative, as the payment sheets say it: Merve'ye,
// Ceyda'ya, Gül'e, Elif'e. The last vowel picks a or e, a final vowel
// takes a y; the -al names that soften their l (Kemal'e) are listed.
const SOFT_L = new Set(["kemal", "cemal", "celal", "bilal", "hilal", "nihal", "meral"]);
export function trDative(name: string): string {
  const last = (name.trim().split(/\s+/).pop() ?? "").toLocaleLowerCase("tr-TR");
  const vowels = [...last].filter((c) => "aıoueiöü".includes(c));
  const front = SOFT_L.has(last) || "eiöü".includes(vowels.at(-1) ?? "e");
  const y = "aıoueiöü".includes(last.at(-1) ?? "") ? "y" : "";
  return `${name.trim()}'${y}${front ? "e" : "a"}`;
}

// A name in the genitive, as the add sheets say it: Merve'nin,
// Seda'nın, Rayan'ın, Elif'in, Koç'un, Gül'ün. The last vowel picks
// ı/i/u/ü, a final vowel takes an n first.
export function trGenitive(name: string): string {
  const last = (name.trim().split(/\s+/).pop() ?? "").toLocaleLowerCase("tr-TR");
  const v = [...last].filter((c) => "aıoueiöü".includes(c)).at(-1) ?? "e";
  const suffix = "aı".includes(v) ? "ın" : "ei".includes(v) ? "in" : "ou".includes(v) ? "un" : "ün";
  const n = "aıoueiöü".includes(last.at(-1) ?? "") ? "n" : "";
  return `${name.trim()}'${n}${suffix}`;
}

// One row of "Son seanslar": the day, what happened (another
// danışman's name and a kind other than a regular seans are said),
// and on the right the fee and what of it is open.
export type HistoryMoney =
  | {kind: "paid"} // ödendi
  | {kind: "owed"} // borç — none of it paid
  | {kind: "left"; leftTL: number} // kalan … TL
  | {kind: "free"} // ücretsiz
  | {kind: "none"} // an iptal: ücret yok
  | {kind: "unknown"}; // a seans whose figures did not come
export interface HistoryView {
  day: string;
  today: boolean;
  cancelled: boolean;
  sub: string;
  feeTL: number | null;
  money: HistoryMoney;
}
export function historyView(row: SessionHistoryRow, providerId: string, today: string):
  HistoryView {
  const other = row.providerId !== providerId && row.providerName ? row.providerName : null;
  const base = {day: trDayName(row.date), today: row.date === today};
  if (row.status === "cancelled") {
    return {...base, cancelled: true, feeTL: null, money: {kind: "none"},
      sub: ["İptal", row.reason, other].filter(Boolean).join(" · ")};
  }
  const kind = row.kind && row.kind !== "regular" ? SERVICE_KIND_TR[row.kind] ?? row.kind : null;
  const sub = ["Yapıldı", kind, other].filter(Boolean).join(" · ");
  const fee = row.feeTL;
  const left = row.remainingTL;
  const money: HistoryMoney = fee === null || left === null ? {kind: "unknown"} :
    fee === 0 ? {kind: "free"} :
    left <= 0 ? {kind: "paid"} :
    left >= fee ? {kind: "owed"} : {kind: "left", leftTL: left};
  return {...base, cancelled: false, sub, feeTL: fee, money};
}

// A figure the desk types, the way the screen prints it: "13.000",
// "13000", "1.500,50". The dot groups thousands, the comma is the
// decimal (a comma with nothing after it yet reads as none, the way
// "1.250," is on the way to "1.250,50"); a dot that does not group
// thousands is not read as a decimal point — it is refused. null for
// anything that is not an amount above 0.
export function parseTL(text: string): number | null {
  const t = text.replace(/\s+/g, "").replace(/TL$/i, "");
  const m = /^(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{0,2}))?$/.exec(t);
  if (!m) return null;
  const n = Number(`${m[1].replace(/\./g, "")}.${m[2] || "0"}`);
  return n > 0 ? n : null;
}

// The WhatsApp mark: the guardian's answer when there is one, else
// where the message got to. Nothing when nothing was sent. A
// confirmation the desk took on the phone says so.
export function waMark(state: string, source?: string | null): Sign | null {
  switch (state) {
  case "none": return null;
  case "confirmed": return source === "phone" ?
    {icon: "✔", word: "Telefonda onaylandı"} : {icon: "✔", word: "WhatsApp: onayladı"};
  case "declined": return {icon: "✖", word: "WhatsApp: gelmiyor"};
  case "text": return {icon: "✉", word: "WhatsApp: yanıt yazdı — okuyun"};
  case "failed": return {icon: "!", word: "WhatsApp: gönderilemedi"};
  case "pending":
  case "sent":
  case "delivered":
  case "read":
    return {icon: "…", word: "WhatsApp: soruldu, yanıt bekleniyor"};
  default: return {icon: "…", word: `WhatsApp: ${state}`};
  }
}

// ── The actions' words ────────────────────────────────────

// The channels the sheet offers. A channel the desk cannot use today
// stays on the sheet, greyed, with its reason — so nobody looks for it.
// Havale / EFT goes to Seda's IBAN (Nefes has no corporate account):
// the web sends "bank", the door books it iban_owner.
export const CHANNELS = [
  {channel: "cash", word: "Nakit", off: null},
  {channel: "pos", word: "POS", off: "POS şu an kullanılamıyor"},
  {channel: "bank", word: "Havale / EFT", off: null},
] as const;

// A havale as the journal holds it: iban_owner, Seda's IBAN, where the
// masa books it — or bank, where it booked it until 11.10.2026.
export const isHavale = (channel: string): boolean =>
  channel === "iban_owner" || channel === "bank";

// "⇄ Nakit'e / Havale'ye çevir": where the tahsilat taken with a seans
// can go — nakit ⇄ havale, nothing else (POS and a danışman's IBAN are
// chat jobs). In the wire's words: "bank" is havale.
export function flipTarget(channel: string | null): "cash" | "bank" | null {
  if (channel === "cash") return "bank";
  return channel !== null && isHavale(channel) ? "cash" : null;
}

// İptal: the quick reasons the desk hears fifty times a day; "diğer"
// opens a text box in their place. "hava koşulları" from 15.10.2026
// (Onur).
export const CANCEL_REASONS = [
  "hasta", "şehir dışı", "haber vermeden gelmedi", "danışman izinli", "hava koşulları",
] as const;
export type CancelReason = typeof CANCEL_REASONS[number];

export const CANCELLED_BY_OPTIONS = [
  {who: "client", word: "veli"},
  {who: "provider", word: "danışman"},
  {who: "clinic", word: "klinik"},
] as const;

// Who a quick İptal reason names while the desk has not picked "İptal
// eden" itself: "danışman izinli" is the danışman's, every other the
// family's (the segment's default) — a two-tap iptal never writes
// "veli: danışman izinli".
export const cancelledByOf = (reason: string): CancelledBy =>
  reason === "danışman izinli" ? "provider" : "client";

// What "✔ Onaylandı (telefon)" came to, for the toast.
export const PHONE_OUTCOME_TR: Record<string, string> = {
  confirmed: "telefonda onaylandı",
  already_confirmed: "zaten onaylı",
  undone: "telefon onayı geri alındı",
  nothing_to_undo: "geri alınacak onay yok",
  not_phone: "onayı veli WhatsApp'tan verdi",
};

// What one WhatsApp send came to, for the toast.
export const WA_OUTCOME_TR: Record<ConfirmOutcome, string> = {
  sent: "WhatsApp onayı gönderildi",
  failed: "WhatsApp gönderilemedi",
  already_sent: "Bu seans için zaten soruldu",
  in_flight: "Gönderim zaten sürüyor",
  no_client: "Danışan bulunamadı",
  not_in_pilot: "Bu danışan pilotta değil",
  no_contact: "WhatsApp için tanımlı kişi yok",
  no_consent: "Kişinin WhatsApp izni yok",
  no_phone: "Kişinin cep numarası yok ya da okunmuyor",
  opted_out: "Bu numara DURDUR demiş",
};

// ── The drawer's words (1.5) ──────────────────────────────

export const CATEGORY_TR: Record<string, string> = {
  session: "seans", behaviour: "davranış", progress: "gelişim", family: "aile",
  other: "diğer",
};

// 1=Pazartesi … 7=Pazar, as the schedule stores it.
export const WEEKDAY_TR = [
  "", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar",
];

export const RECURRENCE_TR: Record<string, string> = {
  weekly: "her hafta", biweekly: "iki haftada bir",
};

export const FORMAT_TR: Record<string, string> = {individual: "bireysel", group: "grup"};

export const ROOM_TR: Record<string, string> = {
  sensory: "duyu odası", large: "büyük oda", small: "küçük oda",
};

// Where a record came from — the cancellation's `source`.
export const SOURCE_TR: Record<string, string> = {
  mcp: "sohbet", http: "gün görünümü", whatsapp: "WhatsApp", job: "otomatik",
  backfill: "aktarım",
};

// The channel as a noun (channelWord in useActions is the sentence form).
// Both havale channels read the same: to the desk they are one thing.
export const CHANNEL_TR: Record<string, string> = {
  cash: "nakit", pos: "POS", bank: "havale/EFT",
  iban_owner: "havale/EFT", iban_provider: "IBAN (danışman)",
};

// The day's tahsilat by channel as the desk names them, for the strip:
// bank and iban_owner add up to one havale/EFT figure. Largest first;
// a channel that came to nothing is left out.
export function channelTotals(byChannel: Record<string, number>): [string, number][] {
  const sums = new Map<string, number>();
  for (const [channel, tl] of Object.entries(byChannel)) {
    const word = CHANNEL_TR[channel] ?? channel;
    sums.set(word, Math.round(((sums.get(word) ?? 0) + tl) * 100) / 100);
  }
  return [...sums].filter(([, tl]) => tl !== 0).sort((a, b) => b[1] - a[1]);
}

// Where money sits — the totals strip's expense source.
export const HOLDER_TR: Record<string, string> = {
  kasa: "kasadan", bank: "bankadan", owner: "Seda'nın hesabından",
};

export const SERVICE_KIND_TR: Record<string, string> = {
  regular: "seans", evaluation: "değerlendirme", makeup: "telafi",
  opening: "eski sistemden",
};

// A WhatsApp confirmation's way: where the message got to, what the
// guardian did, what the tap did to the calendar.
export const DELIVERY_TR: Record<string, string> = {
  pending: "gönderiliyor", sent: "gönderildi", delivered: "iletildi", read: "okundu",
  failed: "gönderilemedi", phone: "WhatsApp gönderilmedi",
};
export const WA_REPLY_TR: Record<string, string> = {
  confirmed: "onayladı", declined: "gelmiyor dedi", text: "yazdı",
};
export const AUTO_CANCEL_TR: Record<string, string> = {
  cancelled: "seans takvimden düşürüldü", restored: "seans takvime geri kondu",
  group_session: "grup seansı — takvime elle bakılacak",
  date_mismatch: "tarih uyuşmadı — takvime elle bakılacak",
  failed: "takvim işlemi başarısız — elle bakılacak",
};

// Dates as the desk writes them: "24.09", "24.09.2026", "24.09 19:10"
// (Istanbul — Türkiye is permanently UTC+3).
export const trDay = (date: string): string => `${date.slice(8, 10)}.${date.slice(5, 7)}`;
export const trDayYear = (date: string): string =>
  `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`;
export function trStamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const ist = new Date(ms + 3 * 3600e3).toISOString();
  return `${ist.slice(8, 10)}.${ist.slice(5, 7)} ${ist.slice(11, 16)}`;
}

// The door's error codes, said once for the toast; the message that
// comes with them is appended.
export const ERROR_CODE_TR: Record<string, string> = {
  not_allowed: "Yetkiniz yok",
  not_found: "Bulunamadı",
  validation: "Geçersiz istek",
  conflict: "Bu arada değişti — yenileyip tekrar deneyin",
  duplicate_suspect: "Benzer bir kayıt zaten var",
  terms: "Bu çift için ücret tanımsız",
  locked_month: "Ay kilitli",
  kasa_negative: "Kasa eksiye düşer",
  calendar_refused: "Takvim kabul etmedi",
  unavailable: "Sunucuya ulaşılamadı",
  internal: "Beklenmeyen hata",
  unauthenticated: "Oturum düştü — yeniden girin",
  not_registered: "Hesap kayıtlı değil",
};
